import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { ModelUsage } from '../../types';

interface ModelUsageChartProps {
  data: ModelUsage[];
  type?: 'pie' | 'bar';
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

export const ModelUsageChart: React.FC<ModelUsageChartProps> = ({ 
  data, 
  type = 'pie' 
}) => {
  const formatCost = (value: number) => `$${value.toFixed(2)}`;

  if (type === 'bar') {
    return (
      <div className="chart-container">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Model Usage Comparison</h3>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="model" 
              stroke="#6b7280"
              fontSize={12}
            />
            <YAxis 
              stroke="#6b7280"
              fontSize={12}
            />
            <Tooltip 
              formatter={(value: number, name: string) => {
                if (name === 'cost') return [formatCost(value), 'Cost'];
                if (name === 'tokens') return [`${(value / 1000).toFixed(0)}k`, 'Tokens'];
                return [value, 'Requests'];
              }}
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
            />
            <Bar dataKey="requests" fill="#3b82f6" name="Requests" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div className="chart-container">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Model Usage Distribution</h3>
      <div className="flex items-center justify-between">
        <ResponsiveContainer width="60%" height={300}>
          <PieChart>
            <Pie
              data={data as any}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={120}
              paddingAngle={2}
              dataKey="percentage"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip 
              formatter={(value: number) => [`${value}%`, 'Usage']}
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        
        <div className="w-1/3 space-y-3">
          {data.map((item, index) => (
            <div key={item.model} className="flex items-center justify-between">
              <div className="flex items-center">
                <div 
                  className="w-4 h-4 rounded-full mr-3"
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{item.model}</span>
              </div>
              <div className="text-right">
                <div className="text-sm font-bold text-gray-900 dark:text-gray-100">{formatCost(item.cost)}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{item.requests} requests</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};